import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Loader2, AlertTriangle, Mail, Clock, Check } from "lucide-react";
import { toast } from "sonner";
import { Layout } from "@/components/Layout";
import { Seo } from "@/components/Seo";
import { RadarChartCard } from "@/components/charts/RadarChartCard";
import { PriorityMatrix, MatrixLegend } from "@/components/charts/PriorityMatrix";
import { Input } from "@/components/ui/input";
import { getResult, registerPaidInterest } from "@/lib/api";

export default function FreeResults() {
  const { resultId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [interestEmail, setInterestEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [registered, setRegistered] = useState(false);

  useEffect(() => {
    if (!resultId) { setLoading(false); return; }
    getResult(resultId).then(setData).catch(() => toast.error("Couldn't load results.")).finally(() => setLoading(false));
  }, [resultId]);

  if (loading) return <Centered><Loader2 className="h-8 w-8 animate-spin text-[#1A6B5A]" /></Centered>;

  if (!resultId || !data) {
    return (
      <Layout>
        <div className="max-w-xl mx-auto px-6 py-32 text-center">
          <h1 className="font-heading text-3xl font-bold text-[#1B3A6B] mb-4">No results yet</h1>
          <p className="text-[#3E577A] mb-8">Take the free Intelligent SCM Scan to see your maturity profile.</p>
          <button onClick={() => navigate("/scan")} className="bg-[#1A6B5A] text-white rounded-sm px-7 py-3.5 font-semibold">Start Free Assessment</button>
        </div>
      </Layout>
    );
  }

  const { results } = data;
  const { overall, radar, matrix_l2, functions } = results;

  const submitInterest = async (e) => {
    e.preventDefault();
    if (!interestEmail) return;
    setSubmitting(true);
    try {
      const r = await registerPaidInterest(interestEmail, resultId);
      toast.success(r.message);
      setRegistered(true);
      setInterestEmail("");
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Layout>
      <Seo
        title="Your SCM Planning Maturity Results | SCMBrain"
        description="Your free Intelligent SCM Scan results — maturity radar, Transformation Priority Matrix, and sub-function score breakdown."
        path="/results/free"
        noindex
      />
      {/* OVERALL BANNER */}
      <section className="border-b border-[#E5E7EB]">
        <div className="max-w-5xl mx-auto px-6 md:px-12 py-16 text-center">
          <p className="text-xs uppercase tracking-[0.2em] text-[#3E577A] font-semibold mb-4">Your SCM Planning Maturity</p>
          <div className="flex items-center justify-center gap-2">
            <span className="font-heading text-7xl font-extrabold" style={{ color: overall.level.color }}>{overall.oms}</span>
            <span className="text-3xl text-[#94A3B8] font-heading">/ 9.0</span>
          </div>
          <div className="mt-4 inline-block px-4 py-1.5 rounded-full text-white font-semibold text-sm" style={{ background: overall.level.color }}>
            Level {overall.level.number} · {overall.level.name}
          </div>
          <p className="mt-6 text-[#3E577A] max-w-2xl mx-auto leading-relaxed">{overall.interpretation}</p>
          <p className="mt-5 text-xs text-[#94A3B8] max-w-2xl mx-auto">
            Your <span className="font-semibold text-[#3E577A]">Operational Maturity Score (OMS)</span> measures how mature
            each process is (lower is more mature). Your <span className="font-semibold text-[#3E577A]">Process Environment Score (PES)</span> measures
            how complex its environment is.
          </p>
        </div>
      </section>

      {/* RADAR */}
      <section className="max-w-5xl mx-auto px-6 md:px-12 py-16">
        <h2 className="font-heading text-2xl font-bold text-[#1B3A6B] mb-2">Maturity by sub-function</h2>
        <p className="text-[#3E577A] mb-6 text-sm">Fuller shape = stronger maturity across the planning functions you assessed.</p>
        <div className="bg-[#F4F4F4] border border-[#E5E7EB] rounded-sm p-6">
          <RadarChartCard data={radar} height={420} />
        </div>
      </section>

      {/* MATRIX */}
      <section className="bg-[#F4F4F4] border-y border-[#E5E7EB]">
        <div className="max-w-5xl mx-auto px-6 md:px-12 py-16">
          <h2 className="font-heading text-2xl sm:text-3xl font-bold text-[#1B3A6B] mb-2">Transformation Priority Matrix</h2>
          <p className="text-[#3E577A] mb-8 max-w-2xl">Where each sub-function sits on maturity vs complexity — and where you should focus first.</p>
          <div className="bg-white border border-[#E5E7EB] rounded-sm p-4 md:p-8">
            <PriorityMatrix data={matrix_l2} />
            <MatrixLegend />
          </div>
        </div>
      </section>

      {/* L2 BREAKDOWN */}
      <section className="max-w-5xl mx-auto px-6 md:px-12 py-16">
        <h2 className="font-heading text-2xl font-bold text-[#1B3A6B] mb-6">Score breakdown</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {functions.flatMap((f) => f.l2).map((l2) => (
            <div key={l2.name} data-testid="l2-breakdown-card"
              className="border rounded-sm p-5 bg-white"
              style={{ borderColor: l2.critical_gaps.length ? "#EF4444" : "#E5E7EB", borderLeftWidth: 4, borderLeftColor: l2.level.color }}>
              <div className="flex items-start justify-between gap-2">
                <h3 className="font-heading font-bold text-[#1B3A6B] text-sm leading-snug">{l2.name}</h3>
                {l2.critical_gaps.length > 0 && (
                  <span className="shrink-0 inline-flex items-center gap-1 text-[10px] font-bold text-[#EF4444] bg-[#FEE2E2] px-2 py-0.5 rounded-full">
                    <AlertTriangle className="h-3 w-3" /> Critical
                  </span>
                )}
              </div>
              <div className="mt-3 flex items-center gap-4 text-sm">
                <div><span className="text-[#94A3B8] text-xs">OMS</span><div className="font-bold text-[#1B3A6B]">{l2.oms}</div></div>
                <div><span className="text-[#94A3B8] text-xs">PES</span><div className="font-bold text-[#1B3A6B]">{l2.pes}</div></div>
                <div className="ml-auto text-xs font-semibold px-2 py-1 rounded-sm text-white" style={{ background: l2.level.color }}>
                  L{l2.level.number} {l2.level.name}
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* PAID REPORT — COMING SOON */}
      <section className="bg-[#1B3A6B]">
        <div className="max-w-3xl mx-auto px-6 md:px-12 py-20 text-center">
          <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.18em] text-[#1A6B5A] bg-[#1A6B5A]/15 px-3 py-1 rounded-full">
            <Clock className="h-3.5 w-3.5" /> Coming Soon
          </span>
          <h2 className="mt-5 font-heading text-3xl sm:text-4xl font-bold text-white leading-tight">
            Full AI Diagnostic Report — Coming Soon
          </h2>
          <p className="mt-4 text-[#C5D2E6] max-w-xl mx-auto leading-relaxed">
            We are finalising our payment integration. Register your interest and we'll notify you the
            moment it goes live.
          </p>

          {registered ? (
            <div data-testid="interest-success" className="mt-8 inline-flex items-center gap-2 bg-white/10 border border-white/15 rounded-sm px-5 py-3 text-[#E2E8F0]">
              <Check className="h-4 w-4 text-[#1A6B5A]" /> You're on the list — we'll be in touch.
            </div>
          ) : (
            <form onSubmit={submitInterest} className="mt-8 flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
              <div className="relative flex-1 w-full">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#94A3B8]" />
                <Input
                  data-testid="interest-email-input" type="email" required
                  aria-label="Email address"
                  placeholder="you@company.com" value={interestEmail}
                  onChange={(e) => setInterestEmail(e.target.value)}
                  className="pl-9 bg-white border-0"
                />
              </div>
              <button
                data-testid="interest-submit-btn" type="submit" disabled={submitting}
                className="bg-[#1A6B5A] text-white hover:bg-[#135245] transition-colors rounded-sm px-6 py-3 font-semibold disabled:opacity-60 whitespace-nowrap inline-flex items-center justify-center gap-2"
              >
                {submitting ? (<><Loader2 className="h-4 w-4 animate-spin" /> Submitting…</>) : "Submit"}
              </button>
            </form>
          )}

          <p className="mt-8 text-xs text-[#8FA3C0] max-w-2xl mx-auto">
            Assessment results are for informational purposes only and do not constitute professional
            consulting advice. See our <a href="/terms" className="underline">Terms of Use</a>.
          </p>
        </div>
      </section>
    </Layout>
  );
}

const Centered = ({ children }) => (
  <div className="min-h-screen flex items-center justify-center bg-white">{children}</div>
);
